/**
 * THE ONE AUTHORIZED DOMAIN BOUNDARY — Capability Definition.
 *
 * FE-1 Stage Work Item §7.3 (Domain-Oriented Architecture) requires each domain
 * boundary to "declare all five fields in source: NAME, CONSUMES, OWNS, MUST NOT
 * OWN, DEPENDS ON", and states that FE1-W0 introduces EXACTLY ONE domain
 * boundary: Capability Definition. This module is that declaration. It declares;
 * it computes nothing.
 *
 * WHY THE DECLARATION IS SOURCE AND NOT A COMMENT
 *   The boundary's most load-bearing statement is what it MUST NOT OWN. A comment
 *   stating it can be deleted without failing anything; a source declaration is
 *   reviewable, and the assertions at the bottom of this file fail the typecheck
 *   if the five required fields stop being exactly the five required fields.
 *
 * A SECOND DIRECTORY UNDER `frontend/src/domain/` IS FORBIDDEN
 *   FE1-W0 Path Boundary v1.3 §6.1 and its §17 STOP condition 9. This is the only
 *   domain boundary, and `capability-definition/` is its only directory.
 */

/**
 * The five fields FE-1 Stage Work Item §7.3 requires a domain boundary to declare.
 *
 * `CONSUMES` and `MUST_NOT_OWN` are the two that carry the boundary's meaning: the
 * first says what the layer is allowed to read, the second says which decisions
 * belong somewhere else.
 */
export interface DomainBoundaryDeclaration {
  /** The product-domain name. Named after the domain, NOT after a screen. */
  readonly NAME: string;
  /** What this boundary reads. Everything else is out of bounds. */
  readonly CONSUMES: readonly string[];
  /** What this boundary decides. */
  readonly OWNS: readonly string[];
  /** What this boundary is forbidden to decide, although it sits adjacent. */
  readonly MUST_NOT_OWN: readonly string[];
  /** What this boundary may import. */
  readonly DEPENDS_ON: readonly string[];
}

/**
 * The Capability Definition boundary.
 *
 * DERIVES ONLY FROM FROZEN CONTRACT SEMANTICS
 *   FE-1 Stage Work Item §7.5: DOMAIN STATE is "owned by the domain layer;
 *   derived ONLY from frozen contract semantics; deterministic given the same
 *   server state". The projection in `./projection.ts` is therefore a pure,
 *   total function of the consumer observation — no I/O, no transport, no timer,
 *   no clock read, no ambient state.
 */
export const CAPABILITY_DEFINITION_BOUNDARY: DomainBoundaryDeclaration = Object.freeze({
  NAME: 'Capability Definition',

  CONSUMES: Object.freeze([
    'the Phase A semantic state system (`frontend/src/shared/semantic/**`) — the presence, freshness, authority and frontend-condition axes, read-only',
    'the Phase B typed consumer observation (`frontend/src/features/capability-equipment/definition/consumer/**`) — a `ReadModelObservation` over the frozen snapshot type, read-only',
    'the frozen contract vocabulary as already mirrored by the consumer layer: AVAILABLE / UNKNOWN / UNAVAILABLE, NON_AUTHORITY, DRAFT / PUBLISHED / DEACTIVATED',
  ]),

  OWNS: Object.freeze([
    'the Capability Definition DOMAIN STATE — an explicit four-variant discriminated union over the read result',
    'the projection from consumer state to domain state, and nothing beyond it',
    'the domain reading of `definitionStatus` AS definition lifecycle, and the statement that lifecycle is not readiness',
    'the domain reading of the freshness caveat the frozen read plane does not publish',
    'the domain reading of the version counters as opaque displayed values (see `./version-precision.ts`)',
  ]),

  MUST_NOT_OWN: Object.freeze([
    'the contract mirror, the adapter port, the fixture data, or any transport — those belong to Phase B and to no domain layer',
    'the MEANING of any of the eight adopted backend fields; the domain reads them and may not redefine them (FE-1 Stage Work Item §7.1, Contract First)',
    'operational availability, readiness, eligibility, assignment, commitment, health or authorization — none of these is a frozen contract fact, and a domain projection that produced one would be inventing a backend fact',
    'whether a state is an error, in the sense of a contract outcome — the frozen contract has no error status, and a reader failure is reported by the contract as UNAVAILABLE',
    'any success predicate, elevation function or second authority value',
    'routing, a route, a production URL, or any second vertical',
    'any mutating action or command affordance',
  ]),

  DEPENDS_ON: Object.freeze([
    '`frontend/src/shared/semantic/**` — the single authority for the Unknown / Empty / Loading / Error / Stale / Degraded / Unavailable / Non-Authority separations (FE-1 Stage Work Item §4.1 E)',
    '`frontend/src/features/capability-equipment/definition/consumer/**` — for its TYPES only, and only so the domain state can carry the frozen snapshot without re-modelling it',
  ]),
});

/* ------------------------------------------------------------------------- *
 * Compile-time assertions of the declaration's shape.
 *
 * They emit no runtime code. A missing field, or a sixth field, fails the
 * typecheck — which is the point of declaring the boundary in source.
 * ------------------------------------------------------------------------- */

/** Fails to compile unless `T` is exactly `true`. */
type AssertTrue<T extends true> = T;

/** `true` only when the two unions have exactly the same members. */
type IsExactly<TLeft, TRight> = [TLeft] extends [TRight]
  ? [TRight] extends [TLeft]
    ? true
    : false
  : false;

/** FE-1 Stage Work Item §7.3: all five fields, and no sixth. */
export type BoundaryDeclaresExactlyTheFiveRequiredFields = AssertTrue<
  IsExactly<
    keyof DomainBoundaryDeclaration,
    'NAME' | 'CONSUMES' | 'OWNS' | 'MUST_NOT_OWN' | 'DEPENDS_ON'
  >
>;

/**
 * That each field of the DECLARATION is populated is a runtime fact about
 * `CAPABILITY_DEFINITION_BOUNDARY`, not a type fact, so it is asserted by a test
 * in `frontend/tests/domain/boundary.test.ts` rather than here. A type cannot see
 * whether an array is empty.
 */
